/**
 * Diplomacy, as data. No behaviour lives here — the verbs are in
 * `diplomacy.ts`, `war.ts` and `diploturn.ts`.
 *
 * This file deliberately imports nothing. `types.ts` needs `DiplomacyState` to
 * put it on `CampaignState`, and `diplomacy.ts` needs `CampaignState` to do
 * anything at all; splitting the shapes out is what keeps that from being a
 * cycle. It also means the whole vocabulary of the system can be read in one
 * sitting without chasing an import.
 *
 * The model is EU4's, taken seriously and then cut down. What survived, and the
 * reasoning, is recorded beside each piece.
 */

// ── Why a war is being fought ──────────────────────────────────────────────

/**
 * A casus belli is a *reason*, and in this game it is derived from the board
 * rather than fabricated over time.
 *
 * EU4 has you spend a diplomat for months to manufacture a claim. That is a
 * timer with a UI, and it buys one thing: the feeling that a war was prepared.
 * The board can give that for free — if you border them you can want their
 * ground, if they took something of yours you can want it back, if they keep a
 * different faith you can say so. `casusBelliAgainst` reads those off the map,
 * so the answer to "why can I not declare this war cleanly" is always something
 * the player can see rather than a cooldown they have to remember.
 *
 * `None` is legal. A crown may always march without a pretext; it simply does it
 * in front of everyone, and the realm's opinion of it moves accordingly. That is
 * the grimmer world's version of EU4's stability hit.
 */
export const CasusBelli = {
  /** No pretext at all. Permitted, and remembered. */
  None: 0,
  /** They are next door and their ground would be better held by you. */
  Conquest: 1,
  /** Ground that was yours before it was theirs. */
  Reconquest: 2,
  /** Their faith, and the fact that it is not yours. */
  HolyWar: 3,
  /** A marriage stood long enough to become an argument about succession. */
  Claim: 4,
  /** They are a subject of somebody, and you would rather they were yours. */
  Subjugation: 5,
} as const;
export type CasusBelliId = (typeof CasusBelli)[keyof typeof CasusBelli];

export const CB_NAMES: Record<number, string> = {
  [CasusBelli.None]: 'No pretext',
  [CasusBelli.Conquest]: 'Conquest',
  [CasusBelli.Reconquest]: 'Reconquest',
  [CasusBelli.HolyWar]: 'Holy war',
  [CasusBelli.Claim]: 'Claim on the throne',
  [CasusBelli.Subjugation]: 'Subjugation',
};

// ── What a war can be settled for ──────────────────────────────────────────

/**
 * A peace term is one demand, not a basket of them.
 *
 * EU4 lets you assemble a peace out of a dozen simultaneous demands, priced
 * against a single pool of war score. That is a spreadsheet, and it is a
 * spreadsheet the AI is famously bad at. One demand per peace is the cut: it
 * makes every peace a sentence the chronicle can print, it makes the AI's choice
 * legible ("they took Rookmoor and went home"), and it means a war that is worth
 * two provinces has to be fought twice — which is a better shape for a game about
 * grinding attrition than a single crushing settlement is.
 */
export const PeaceTerm = {
  /** Nobody owes anybody anything. The map stands as it is. */
  White: 0,
  /** One province the loser still holds changes hands. */
  Cede: 1,
  /** Gold, out of the loser's treasury and into the winner's. */
  Tribute: 2,
  /** The loser renounces its alliances and stands alone for a while. */
  Concessions: 3,
  /** The loser bends the knee: a vassal that pays and fights. */
  Vassalise: 4,
  /** Two crowns, one head. Needs a claim and a marriage behind it. */
  Union: 5,
} as const;
export type PeaceTermId = (typeof PeaceTerm)[keyof typeof PeaceTerm];

export const TERM_NAMES: Record<number, string> = {
  [PeaceTerm.White]: 'White peace',
  [PeaceTerm.Cede]: 'Cede a province',
  [PeaceTerm.Tribute]: 'Tribute',
  [PeaceTerm.Concessions]: 'Concessions',
  [PeaceTerm.Vassalise]: 'Vassalage',
  [PeaceTerm.Union]: 'Personal union',
};

// ── Standing arrangements ──────────────────────────────────────────────────

/**
 * What one crown holds over another.
 *
 * Two kinds, where EU4 has a dozen. A **vassal** is a crown that lost and pays
 * for it: it hands over a share of what it collects, it fights its liege's wars
 * without being asked, and it may not start one of its own. A **union junior**
 * is a crown that was *inherited* rather than beaten: it pays almost nothing,
 * because the point of a union is not money — it is that its hosts are your
 * hosts and its enemies are your enemies, and that one day you may simply keep it.
 *
 * March, client state, tributary, colony and the rest are cut. Every one of them
 * is the same two levers — what they pay, what they owe in war — at a different
 * setting, and a menu of five subject types that differ by a percentage is a menu
 * rather than a system.
 */
export const SubjectKind = {
  None: 0,
  Vassal: 1,
  Union: 2,
} as const;
export type SubjectKindId = (typeof SubjectKind)[keyof typeof SubjectKind];

export const SUBJECT_NAMES: Record<number, string> = {
  [SubjectKind.None]: 'Independent',
  [SubjectKind.Vassal]: 'Vassal',
  [SubjectKind.Union]: 'Junior partner',
};

/**
 * A standing agreement between two crowns.
 *
 * A list rather than a matrix, and that is the scale decision in this file. The
 * opinion table has to be `n²` because every crown has a view of every other
 * crown whether or not anything has ever happened between them. Treaties are the
 * opposite: at thirty kingdoms there will be a few dozen of them, and walking a
 * few dozen every turn is linear in what actually exists rather than in what
 * could.
 *
 * `a < b`, always, so a pair has exactly one representation.
 */
export const TreatyKind = {
  /** Called to arms, and expected to come. */
  Alliance: 0,
  /** Two houses joined. Opinion, and eventually an argument about inheritance. */
  RoyalMarriage: 1,
} as const;
export type TreatyKindId = (typeof TreatyKind)[keyof typeof TreatyKind];

export interface Treaty {
  kind: number;
  /** Nation indices, `a < b`. */
  a: number;
  b: number;
  /** Turn it was signed. Marriages need age before they mean anything. */
  since: number;
}

/**
 * One crown's leave for another to walk on its ground.
 *
 * Directional, which is why it is not a `Treaty`. Every `Treaty` in this system
 * is stored once per pair with `a < b` because every one of them is symmetric —
 * two crowns are allied or they are not. Passage is the opposite: letting your
 * neighbour's host cross your moors is nothing like being let across theirs, and
 * that asymmetry is most of what makes it worth asking for. So `from` is the
 * crown whose ground it is and `to` is the crown that may walk on it, and the
 * two directions are two records.
 *
 * Indefinite. There is no `until`, and that was the decision rather than an
 * omission: a grant with a clock on it lapses on a turn nobody chose, which
 * makes the interesting event — an army suddenly standing where it may not —
 * arrive as bookkeeping rather than as somebody's decision. A grant stands until
 * it is taken back, and taking it back is a thing a crown *does*, in front of
 * everybody, to an army it can see. See `OP_REVOKED_PASSAGE`.
 */
export interface AccessGrant {
  /** The crown whose ground it is. */
  from: number;
  /** The crown that may cross it. */
  to: number;
  /** Turn it was given. Flavour, and the panel prints it. */
  since: number;
}

/**
 * Peace with a clock on it.
 *
 * The single most important object in this file, because it is the thing the old
 * board had no room for. A truce is why a war *ends* rather than merely pausing
 * until somebody has an army again, and it is why the map can have a shape
 * instead of being a permanent brawl. Sparse, like treaties, and expired in one
 * pass per turn.
 */
export interface Truce {
  a: number;
  b: number;
  /** Campaign turn on which it lapses. */
  until: number;
}

// ── Wars ───────────────────────────────────────────────────────────────────

/**
 * A war, as an object rather than as two bits in a matrix.
 *
 * `atWar` on `Nation` survives as the O(1) index over this list — everything
 * that asks "may I shoot at them" reads that — but the war itself is what can be
 * won, lost, joined and ended. `score` is the whole point: a number, from the
 * primary attacker's point of view, that says who is ahead and therefore what
 * they may ask for.
 */
export interface War {
  id: number;
  /** The crown that declared it. Only this one may be peaced out on the attacking side. */
  attacker: number;
  /** The crown it was declared on. */
  defender: number;
  /** Everybody on each side, primary included, in the order they joined. */
  attackers: number[];
  defenders: number[];
  /** A `CasusBelli`. Decides what may be demanded, and what the realm thinks. */
  cb: number;
  /** The province the war was nominally about, or -1. */
  goal: number;
  startTurn: number;
  /** -100..100, from the primary attacker's side. */
  score: number;
  /** The ledger the panel prints. Battles and provinces, each side. */
  battlesWon: number;
  battlesLost: number;
  taken: number;
  lost: number;
  /** Turn a peace was last put to the player, so the AI cannot nag every turn. */
  lastOffer: number;
}

// ── Things being asked of somebody ─────────────────────────────────────────

/**
 * An offer sitting on the table.
 *
 * This is how the AI talks to the player. An AI nation deciding something about
 * another AI nation resolves on the spot — there is nobody to ask — but a call
 * to arms, a peace or a proposed alliance aimed at the player has to *wait*, and
 * a thing that waits is state. Offers expire, which is what stops the list
 * growing and what makes ignoring one a decision with a cost.
 */
export const OfferKind = {
  /** An ally is at war and expects you in it. */
  CallToArms: 0,
  /** They want the war over, on these terms. */
  Peace: 1,
  Alliance: 2,
  Marriage: 3,
  /** They are offering to bend the knee, or asking you to. */
  Vassalage: 4,
  /**
   * They want to march a host across your country.
   *
   * `from` is asking `to` for the run of `to`'s ground — so, unlike every other
   * offer here, the crown being asked is the one that would be giving something
   * up, and it is being asked by somebody who is at war with somebody else. That
   * is the whole tension: the answer decides whose country the war is fought in.
   */
  Passage: 5,
} as const;
export type OfferKindId = (typeof OfferKind)[keyof typeof OfferKind];

export interface DiploOffer {
  id: number;
  kind: number;
  /** Who is asking. */
  from: number;
  /** Who is being asked. In practice, the player. */
  to: number;
  /** War id for a call to arms or a peace; -1 otherwise. */
  war: number;
  /** A `PeaceTerm` for a peace offer; -1 otherwise. */
  term: number;
  /** Province at stake for a `Cede`; -1 otherwise. */
  province: number;
  /** Gold at stake for a `Tribute`; 0 otherwise. */
  gold: number;
  /** True when the offer's `from` is the one conceding. */
  fromConcedes: boolean;
  /** Turn after which it lapses, and refusing by silence costs what refusing costs. */
  expires: number;
}

export interface DiplomacyState {
  wars: War[];
  treaties: Treaty[];
  truces: Truce[];
  /** Rights of passage, one record per direction. Sorted by `from` then `to`. */
  access: AccessGrant[];
  offers: DiploOffer[];
  nextWarId: number;
  nextOfferId: number;
}

export function createDiplomacy(): DiplomacyState {
  return {
    wars: [],
    treaties: [],
    truces: [],
    access: [],
    offers: [],
    nextWarId: 1,
    nextOfferId: 1,
  };
}

// ── The numbers ────────────────────────────────────────────────────────────

export const OPINION_MIN = -100;
export const OPINION_MAX = 100;

/**
 * How far opinion moves toward where the board says it should be, per turn.
 *
 * Opinion is stored, not derived, and this is why. A derived opinion is a
 * function of the current board and therefore has no memory: break an alliance
 * and the injury is gone the instant the treaty leaves the list. Stored opinion
 * with a drift means the board sets where a relationship *settles* and events
 * knock it about on the way, so a crown can be structurally your natural friend
 * and still not have forgiven you.
 */
export const OPINION_DRIFT = 3;

/**
 * The structural terms. These decide where a relationship settles.
 *
 * `OP_BASE` is the one that is easy to leave out and must not be: a crown you
 * have never met and have no quarrel with should sit slightly *above* zero, not
 * at it. Without it every term in the table is a penalty, every relationship on
 * the board is negative, and the diplomatic half of the game never starts —
 * which is exactly what the first tuning of these numbers produced, measured over
 * eight realms and a hundred and forty turns apiece: eighteen wars declared and
 * not one marriage signed.
 */
export const OP_BASE = 12;
export const OP_SAME_CONFESSION = 25;
/** Multiplied by the 0..1 hostility between two faiths. */
export const OP_CONFESSION_HOSTILITY = -35;
export const OP_ALLIANCE = 30;
export const OP_MARRIAGE = 20;
export const OP_SUBJECT = 35;
export const OP_TRUCE = 12;
export const OP_AT_WAR = -70;
/** Sharing a border is a reason to dislike somebody. It always has been. */
export const OP_BORDER = -6;
/**
 * And an enemy in common is the oldest reason in the world to get on.
 *
 * The one positive structural term that is not a treaty, and the system needs it
 * badly: without it the only way opinion ever rises is a marriage, so nothing
 * that happens on the map can *make* two crowns friendly and the diplomatic
 * board is decided entirely by the confession table on turn one. With it, a war
 * against a runaway power drives the rest of the realm together, which is the
 * shape a coalition would have had if this game had coalitions.
 */
export const OP_COMMON_ENEMY = 18;

/** The events. These are what the drift is drifting away from. */
export const OP_DECLARED_WAR = -45;
/** Extra, on everyone alive, for a war declared with no pretext at all. */
export const OP_NO_CB_REALM = -14;
export const OP_REFUSED_CALL = -55;
/** What the rest of the realm thinks of a crown that left an ally standing. */
export const OP_ABANDONED_REALM = -12;
export const OP_ANSWERED_CALL = 22;
export const OP_BROKE_ALLIANCE = -30;
/** Per province taken in a peace, on every crown that is not a party to it. */
export const OP_TOOK_LAND_REALM = -16;
export const OP_HUMBLED = -20;

// ── Passage ────────────────────────────────────────────────────────────────

/**
 * A standing grant is a relationship, so it sits in the opinion table.
 *
 * Small on purpose, and applied in both directions when a grant exists either
 * way. It is not a treaty of friendship — a crown that granted passage out of
 * fear does not thereby like the crown it was frightened of — but two countries
 * whose armies are allowed to cross one another have an arrangement, and an
 * arrangement is worth a few points against the border penalty that is almost
 * certainly also in the sum.
 */
export const OP_PASSAGE = 8;

/**
 * What refusing a herald costs the crown that refuses.
 *
 * The whole of "closed borders as a hostile act", and it is deliberately *not* a
 * new mechanic. It moves the asker's opinion of the refuser down, and opinion is
 * already what `DECLARE_SPITE` in `diploturn.ts` reads when it lowers a crown's
 * bar for declaring war on somebody it dislikes. So a crown that says no to a
 * strong neighbour has made itself measurably more likely to be that neighbour's
 * next war, through machinery that already existed and that the player can
 * already read on the panel. A separate "they refused me" casus belli was
 * considered and cut: every crown you would ask is a crown you border, and
 * `CasusBelli.Conquest` is already available against everybody you border.
 */
export const OP_REFUSED_PASSAGE = -10;
/** And what saying yes is worth. Rather less than being asked is worth costing. */
export const OP_GRANTED_PASSAGE = 14;
/**
 * Taking it back, felt by the crown whose hosts are now standing in the open.
 *
 * Much larger than a refusal, and it has to be: a refusal declines to give
 * something, a revocation takes back something that armies were relying on. It
 * is the single most aggressive thing in this file that is not a declaration of
 * war, and it should read that way on the panel.
 */
export const OP_REVOKED_PASSAGE = -25;
/** What the rest of the realm makes of a crown that stranded somebody's army. */
export const OP_REVOKED_REALM = -5;
/** Per month, on the owner of the ground, for every month a host overstays. */
export const OP_TRESPASS = -3;

/**
 * What a crown wants to think of you before it lets your host in.
 *
 * Above the alliance bar. Passage is asked for in wartime by somebody who wants
 * to march an army across your country at a third party, and that is a larger
 * thing to agree to than an alliance — an alliance is a promise about the
 * future, this is a foreign army in your fields next month.
 */
export const PASSAGE_OPINION = 35;
/**
 * Unless they are simply too big to refuse.
 *
 * The other half of the answer to "what does a refusal cost the refuser", and
 * the half that makes the AI's behaviour legible rather than merely polite: a
 * small crown standing between two large ones is *going* to be marched through,
 * and the only question is whether it happens with its leave or after a war it
 * cannot win. So a crown that is outweighed this badly says yes to anybody it
 * does not actively hate.
 */
export const PASSAGE_FEAR_RATIO = 2.5;
/** How much a frightened crown may still dislike you and let you through anyway. */
export const PASSAGE_FEAR_OPINION = -40;
/** Opinion at which a crown takes a standing grant back. */
export const PASSAGE_REVOKE_OPINION = -25;

/**
 * Months a host has to leave ground it may no longer stand on.
 *
 * Three, which is one more than the longest ordinary march on this board's road
 * graph and therefore enough for a host to get out of anywhere it can get out
 * of. Long enough that a peace does not immediately start killing the armies
 * that fought it; short enough that squatting is not a strategy.
 */
export const TRESPASS_GRACE = 3;

/** Nobody may hold more than this many alliances. */
export const MAX_ALLIES = 2;
/** Nor more than this many offers waiting on them, so the list is bounded. */
export const MAX_OFFERS = 12;
/** Turns an offer stands before it lapses, and lapsing counts as a refusal. */
export const OFFER_TURNS = 3;

// ── War score ──────────────────────────────────────────────────────────────

export const WAR_SCORE_MAX = 100;
/** A won battle is worth this, plus a share of what was beaten, capped. */
export const SCORE_BATTLE_BASE = 4;
export const SCORE_BATTLE_PER_POINT = 1 / 220;
export const SCORE_BATTLE_CAP = 14;
/** Taking a province is worth this, before its wealth and its walls. */
export const SCORE_PROVINCE_BASE = 12;
export const SCORE_PROVINCE_CAP = 28;

/**
 * After this many turns a war starts forgetting who won the first battle.
 *
 * Time is the fourth source of war score in EU4 and it is the one that actually
 * ends wars, because without it a stalemate is permanent and two exhausted
 * powers sit at ±5 forever. Here it runs the other way — score bleeds *toward
 * zero* rather than toward the defender — which pushes a long war toward a white
 * peace rather than toward a slow, unearned win for whoever is standing still.
 */
export const WAR_WEARY_AFTER = 18;
export const WAR_WEARY_DECAY = 1;

// ── Peace terms, priced ────────────────────────────────────────────────────

export const COST_WHITE = 0;
export const COST_CONCESSIONS = 30;
export const COST_VASSALISE = 55;
export const COST_UNION = 78;
/** Floor and ceiling on what one province may cost in war score. */
export const COST_CEDE_MIN = 18;
export const COST_CEDE_MAX = 65;
/** Gold a point of war score is worth when it is cashed in as tribute. */
export const GOLD_PER_SCORE = 14;
/** And the most of its purse a beaten crown will ever hand over. */
export const TRIBUTE_MAX_SHARE = 0.6;

/** Truce a white peace buys, and how much longer a harder peace buys. */
export const TRUCE_BASE = 6;
export const TRUCE_PER_COST = 0.18;
export const TRUCE_MAX = 18;

// ── Subjects ───────────────────────────────────────────────────────────────

/** Share of what a vassal collects that goes up the chain, each turn. */
export const VASSAL_TRIBUTE = 0.25;
/** A junior partner pays almost nothing. Its hosts are the point, not its purse. */
export const UNION_TRIBUTE = 0.08;
/** Turns of subjection, and opinion, before a liege may swallow a subject whole. */
export const ANNEX_TURNS = 25;
export const ANNEX_OPINION = 60;
/** A union takes longer to inherit outright than a vassal takes to annex. */
export const UNION_INTEGRATE_TURNS = 45;

// ── Marriages, claims and inheritance ──────────────────────────────────────

/** Turns a marriage must stand before it is an argument about the succession. */
export const CLAIM_MARRIAGE_TURNS = 20;
/** And before a crown may simply fall in, without a war. */
export const UNION_MARRIAGE_TURNS = 34;
/**
 * Chance per turn that an old marriage puts two crowns on one head.
 *
 * Small on purpose. A personal union arriving by inheritance is the single most
 * dramatic thing this system can do to a map, and a dramatic thing that happens
 * reliably is a mechanic rather than an event. At this rate a marriage that has
 * stood since the mid-game will pay off in maybe one campaign in four, which is
 * about the frequency at which a player still tells somebody about it.
 */
export const UNION_INHERIT_CHANCE = 0.006;
